import type { ComponentType, ReactNode } from 'react';
import type { WidgetInstance, WidgetSize, WidgetType } from './types';

export type WidgetConfigProps = { config: Record<string, unknown>; onChange: (c: Record<string, unknown>) => void };
// w/h are the widget's resolved footprint in grid cells, so a render can adapt
// its layout to its size (e.g. a 1x1 KPI stacks vertically).
export type WidgetRenderProps = { config: Record<string, unknown>; w?: number; h?: number; widgetId?: string };

export type WidgetDef = {
  type: WidgetType;
  label: string;
  defaultSize: WidgetSize;
  icon: ReactNode;
  accent: string;
  // Optional per-instance header title (overrides `label` in the card header,
  // icon kept). Used by the BI chart tile to show the chosen chart's name.
  title?: (config: Record<string, unknown>) => string;
  // Optional element rendered on the header row (right of the title, before the
  // drag/menu). Used by the BI chart tile for its "Open in Analytics" link.
  headerAction?: (config: Record<string, unknown>) => ReactNode;
  // Compact tiles (e.g. KPI): tight padding + header hidden in view mode.
  compact?: boolean;
  // Omit from the "Add widget" picker while keeping the def registered so any
  // already-placed instances still render.
  hideFromPicker?: boolean;
  // Default footprint for tiles that don't map to an S/M/L size (overrides
  // defaultSize when a new widget is added and when no w/h is stored).
  defaultWH?: { w: number; h: number };
  // Resize limits (grid cells). If min===max on an axis that axis is locked.
  // Absent = free resize within the global [1,12] x [1,16] bounds.
  bounds?: { minW: number; maxW: number; minH: number; maxH: number };
  // Static RBAC node, or derive one from config (e.g. which BI report is embedded):
  nodeFor?: (config: Record<string, unknown>) => string | undefined;
  ConfigForm: ComponentType<WidgetConfigProps>;
  Render: ComponentType<WidgetRenderProps>;
};

export const widgetRegistry: Partial<Record<WidgetType, WidgetDef>> = {};
export function registerWidget(def: WidgetDef) { widgetRegistry[def.type] = def; }
export function getWidgetDef(type: WidgetType): WidgetDef | undefined { return widgetRegistry[type]; }

// Resolve the RBAC node a widget instance requires (if any):
export function nodeForWidget(w: WidgetInstance): string | undefined {
  const def = getWidgetDef(w.type);
  return def?.nodeFor ? def.nodeFor(w.config) : undefined;
}
